import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { comboService } from '@/services/combo.service';
import { matchComboState } from '../utils/comboMatcher';
import type { ComboPriceBlock, ComboSuggestion, PosComboDefinition } from '../utils/comboMatcher';
import type { OrderItem } from '../types';

const QUERY_KEY = ['combos', 'pos-active'] as const;

/**
 * Khoá ghi nhớ "đã bỏ qua" = tab + combo + số combo.
 *
 * Review round 1, Finding 3: `tabId` PHẢI có mặt trong khoá. `POSPage` chỉ gọi
 * hook này MỘT LẦN (một instance `dismissed` dùng chung cho tối đa 10 tab —
 * xem `usePosTabs`/`MAX_TABS`), nên thiếu `tabId` thì "Bỏ qua" ở tab A âm thầm
 * đè lên tab B nếu hai đơn khớp cùng combo + cùng số lượng — hai thu ngân bán
 * trùng một chương trình khuyến mãi trong ngày là tình huống bình thường, không
 * phải cạnh hiếm.
 *
 * Giỏ đổi làm số combo khác đi thì gợi ý hiện lại — thu ngân bỏ qua "2 combo"
 * không có nghĩa là bỏ qua "3 combo".
 */
function dismissKey(tabId: string, suggestion: ComboSuggestion): string {
  return `${tabId}:${suggestion.combo_id}:${suggestion.unit_count}`;
}

export function useComboSuggestions({
  items,
  isB2B,
  enabled,
  tabId,
}: {
  items: OrderItem[];
  isB2B: boolean;
  enabled: boolean;
  /** Tab đơn đang active — xem `dismissKey`. */
  tabId: string;
}) {
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const { data: definitions = [] } = useQuery<PosComboDefinition[]>({
    queryKey: QUERY_KEY,
    queryFn: () => comboService.posActive(),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !isB2B,
  });

  const { suggestion, blocked } = useMemo(() => {
    // B2B carts get no combo suggestions at all — matchComboState already
    // returns rỗng for isB2B, but short-circuit here too so we never even look
    // at `dismissed`/`definitions` for a B2B cart.
    if (isB2B) {
      return {
        suggestion: undefined as ComboSuggestion | undefined,
        blocked: undefined as ComboPriceBlock | undefined,
      };
    }
    const state = matchComboState(items, definitions, { isB2B });
    const active = state.suggestions.find(
      (candidate) => !dismissed.has(dismissKey(tabId, candidate)),
    );
    // CHỈ nói về combo bị chặn khi KHÔNG có gợi ý nào đang hiện: hai băng cùng
    // lúc là nhiễu, mà thu ngân chỉ thật sự bí khi màn hình không đưa ra gì.
    // Trong số bị chặn, lấy cái đáng tiếc nhất (tiết kiệm lớn nhất).
    const topBlocked = active
      ? undefined
      : [...state.blocked].sort((a, b) => b.saving - a.saving)[0];
    return { suggestion: active, blocked: topBlocked };
  }, [items, definitions, isB2B, dismissed, tabId]);

  const definition = useMemo(
    () => definitions.find((def) => def._id === suggestion?.combo_id),
    [definitions, suggestion],
  );

  const dismiss = useCallback(
    (target: ComboSuggestion) => {
      setDismissed((prev) => new Set(prev).add(dismissKey(tabId, target)));
    },
    [tabId],
  );

  const refresh = useCallback(() => {
    // Chỉ xoá "Bỏ qua" của TAB ĐANG GỌI `refresh()` — không đụng tab khác.
    // `dismissed` là MỘT state dùng chung cho mọi tab (xem comment trên
    // `dismissKey`); xoá sạch cả Set ở đây sẽ vô hiệu hoá chính việc cô lập
    // theo tab vừa thêm phía trên — "Tiếp tục"/đóng/mở tab MỚI ở tab này lại
    // vô tình bật lại banner của một đơn KHÁC đang mở, mà thu ngân đơn đó chưa
    // hề bấm gì.
    setDismissed((prev) => {
      const prefix = `${tabId}:`;
      const next = new Set(prev);
      let changed = false;
      for (const key of prev) {
        if (key.startsWith(prefix)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }, [queryClient, tabId]);

  return { suggestion, blocked, definition, definitions, dismiss, refresh };
}
